import { memo, useState, useRef, useEffect, useContext } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { AuthContext } from '@/context/auth-context'
import { buildOrderUpdates } from '@/utils/reorder'
import { AdsManagerIcon } from '@/components/icons/AdsManagerIcon'
import { RefreshIcon } from '@/components/icons/RefreshIcon'
import { TrashIcon, InfoIcon, MegaphoneIcon, PlusIcon } from '@/components/icons'
import { AddAudienceIcon } from '@/components/icons/AddAudienceIcon'
import CalendarIcon from '@/components/icons/CalendarIcon'
import CircleFilledIcon from '@/components/icons/CircleFilledIcon'
import CircleOutlineIcon from '@/components/icons/CircleOutlineIcon'
import TriangleIcon from '@/components/icons/TriangleIcon'
import { ChevronDownIcon, ChevronRightIcon, Copy, Pencil } from 'lucide-react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { DatePicker } from '@/components/ui/date-picker'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import useAdsManagerStore, { AUDIENCE_TYPE_FILTERS, applyAudienceTypeFilter } from '@/store/adsManagerStore'
import { useAdsManagerData } from './hooks/useAdsManagerData'
import { FUNNEL_STAGES } from '@/utils/ad-campaign-constants'
import { cn } from '@/lib/utils'
import BudgetInput from './BudgetInput'
import LatestActionsCell from './LatestActionsCell'
import CampaignWarning from './CampaignWarning'
import AdvantageLeadsNote from './AdvantageLeadsNote'
import { CAMPAIGN_BUDGET_AD_SET_NOTE, FAIR_SPLIT_NOTE } from '@/utils/advantage-leads'
import HypothesisChip from './HypothesisChip'
import ExclusionNote from './ExclusionNote'
import WarmAudienceNote from './WarmAudienceNote'
import LookalikeNote from './LookalikeNote'
import BudgetMoveNote from './BudgetMoveNote'
import CreativeThumbnail from '@/components/mediaBuying/adCampaign/screens/AdsSelection/CreativeThumbnail'
import { CreativeHoverPreview } from '@/pages/campaigns/creatives/components/CreativeAdPreview'

// 1.5 times smaller than the My Creatives hover preview (1.4), so it fits the viewport
const AD_HOVER_PREVIEW_SCALE = 1.4 / 1.5
import ConfirmationDialog from '@/components/common/ConfirmationDialog'
import { formatCurrencyAutoLocale } from '@/utils/tools'
import { useMediaBuyingContext } from '@/context/media-buying-context'
import { CubeLoader } from '@/components/ui/cube-loader'

const AdsManager = memo(() => {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const skipInitialLoad = searchParams.get('skipInitialLoad') === 'true'
    const {
        dateRange,
        expandedItems,
        loading,
        error,
        setDateRange,
        toggleItemExpanded,
        getFilteredCampaigns,
        computeStageMetrics,
        setItemLoading,
        isItemLoading,
        reorderCampaigns,
        audienceTypeFilter,
        setAudienceTypeFilter,
    } = useAdsManagerStore()
    const { user } = useContext(AuthContext)

    const mediaBuyingContext = useMediaBuyingContext()
    const { connectedPublishers } = mediaBuyingContext
    const publisher = connectedPublishers ? Object.values(connectedPublishers)[0] : null
    const currency = publisher?.currency
    const minDailyBudget = publisher?.min_daily_budget

    // Function to compute campaign budget (sum of all stage budgets)
    const computeCampaignBudget = (campaign) => {
        if (!campaign.stages || campaign.stages.length === 0) return 0

        return campaign.stages.reduce((total, stage) => {
            const stageMetrics = computeStageMetrics(stage)
            return total + (stageMetrics.budget || 0)
        }, 0)
    }

    const campaignId = useParams().id

    const {
        refetch,
        updateAdSetBudget,
        updateCampaignBudget,
        toggleCampaignStatus,
        toggleAdSetStatus,
        toggleAdStatus,
        deleteCampaign,
        deleteAdSet,
        deleteAd,
        updateAdCampaignOrder,
        creativesById,
        pageTitle,
        pageIcon,
    } = useAdsManagerData(campaignId, skipInitialLoad, connectedPublishers)

    // The ad mockup of an ad's creative, as on My Creatives, once the campaign creatives are loaded. Smaller than
    // there so the whole card, including its bottom, stays inside the viewport while hovering.
    const creativeHoverPreview = (creativeId) => {
        const creative = creativesById?.[creativeId]
        if (!creative) return undefined
        return (
            <CreativeHoverPreview
                creative={creative}
                pageTitle={pageTitle}
                pageIcon={pageIcon}
                scale={AD_HOVER_PREVIEW_SCALE}
            />
        )
    }

    const searchedCampaigns = getFilteredCampaigns()
    const campaigns = searchedCampaigns.map((campaign) => applyAudienceTypeFilter(campaign, audienceTypeFilter))

    // Custom date range state - initialize with store values if custom dateRange
    const [customStartDate, setCustomStartDate] = useState(() => {
        return typeof dateRange === 'object' && dateRange.type === 'custom' ? dateRange.startDate : ''
    })
    const [customEndDate, setCustomEndDate] = useState(() => {
        return typeof dateRange === 'object' && dateRange.type === 'custom' ? dateRange.endDate : ''
    })

    // Delete confirmation dialog state
    const [deleteConfirmation, setDeleteConfirmation] = useState({
        isOpen: false,
        item: null,
        type: null,
        parentIds: null,
        isProcessing: false,
    })

    // Track row index for alternating colors
    const rowIndexRef = useRef(0)

    // Drag-and-drop for top-level campaign rows. Visual feedback is applied
    // imperatively (drag/dragover fire continuously, so going through React
    // state here would re-render the entire ads-manager tree per mousemove).
    const dragSourceIdRef = useRef(null)

    const persistCurrentOrder = () => {
        if (!user?.id || !campaignId) return
        const orderedIds = useAdsManagerStore.getState().campaigns.map((c) => c.id)
        // On failure, resync with the server's persisted order rather than leaving
        updateAdCampaignOrder(buildOrderUpdates(orderedIds)).catch(() => refetch())
    }

    const clearDragOverIndicators = () => {
        document
            .querySelectorAll('[data-campaign-row].drag-over-top, [data-campaign-row].drag-over-bottom')
            .forEach((node) => node.classList.remove('drag-over-top', 'drag-over-bottom'))
    }

    const getDropPosition = (event) => {
        const rect = event.currentTarget.getBoundingClientRect()
        return event.clientY < rect.top + rect.height / 2 ? 'before' : 'after'
    }

    const handleCampaignDragStart = (event, draggedId) => {
        dragSourceIdRef.current = draggedId
        event.dataTransfer.effectAllowed = 'move'
        event.dataTransfer.setData('text/plain', draggedId)
        event.currentTarget.classList.add('is-dragging')
    }

    const handleCampaignDragEnd = (event) => {
        dragSourceIdRef.current = null
        event.currentTarget.classList.remove('is-dragging')
        clearDragOverIndicators()
    }

    const handleCampaignDragOver = (event, targetId) => {
        if (!dragSourceIdRef.current || dragSourceIdRef.current === targetId) return
        event.preventDefault()
        event.dataTransfer.dropEffect = 'move'
        const position = getDropPosition(event)
        const node = event.currentTarget
        const wantTop = position === 'before'
        if (node.classList.contains(wantTop ? 'drag-over-top' : 'drag-over-bottom')) return
        node.classList.remove('drag-over-top', 'drag-over-bottom')
        node.classList.add(wantTop ? 'drag-over-top' : 'drag-over-bottom')
    }

    const handleCampaignDragLeave = (event) => {
        event.currentTarget.classList.remove('drag-over-top', 'drag-over-bottom')
    }

    const handleCampaignDrop = (event, targetId) => {
        event.preventDefault()
        event.currentTarget.classList.remove('drag-over-top', 'drag-over-bottom')
        const sourceId = dragSourceIdRef.current
        dragSourceIdRef.current = null
        if (!sourceId || sourceId === targetId) return
        const position = getDropPosition(event)
        reorderCampaigns(sourceId, targetId, position)
        persistCurrentOrder()
    }

    // Keyboard reorder: Alt+ArrowUp / Alt+ArrowDown on a focused campaign row
    // moves it past its neighbour. Provides a path for keyboard / touch users
    // (native HTML5 DnD does not).
    const handleCampaignKeyDown = (event, currentId) => {
        if (!event.altKey || (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')) return
        const stateCampaigns = useAdsManagerStore.getState().campaigns
        const idx = stateCampaigns.findIndex((c) => c.id === currentId)
        if (idx < 0) return
        const neighbourIdx = event.key === 'ArrowUp' ? idx - 1 : idx + 1
        const neighbour = stateCampaigns[neighbourIdx]
        if (!neighbour) return
        event.preventDefault()
        reorderCampaigns(currentId, neighbour.id, event.key === 'ArrowUp' ? 'before' : 'after')
        persistCurrentOrder()
    }

    // Helper function to check if text would overflow 2 lines (rough estimation)
    // This is a simple heuristic - assumes average character width and container width
    const shouldShowTooltip = (text, containerWidth = 350) => {
        if (!text) return false
        // Rough estimation: ~7 characters per 1rem, 2 lines max, accounting for padding
        const maxCharsPerLine = Math.floor(containerWidth / 8) // Approximate characters per line
        const maxChars = maxCharsPerLine * 2
        return text.length > maxChars
    }

    // Clear the skipInitialLoad query parameter after mount
    useEffect(() => {
        if (skipInitialLoad) {
            // Remove the query parameter from URL without causing navigation
            const newSearchParams = new URLSearchParams(searchParams)
            newSearchParams.delete('skipInitialLoad')
            const newUrl = `${window.location.pathname}${newSearchParams.toString() ? '?' + newSearchParams.toString() : ''}`
            window.history.replaceState({}, '', newUrl)
        }
    }, [skipInitialLoad, searchParams])

    // Helper function to get learning phase icon
    const getLearningPhaseIcon = (phrase) => {
        if (!phrase) return null

        switch (phrase) {
            case 'Completed':
                return <CircleFilledIcon size={16} className="tw:text-green-500" />
            case 'Learning':
                return <CircleOutlineIcon size={16} className="tw:text-green-500" />
            case 'Limited Learning':
                return <TriangleIcon size={16} className="tw:text-orange-500" />
            default:
                return null
        }
    }

    // Handle custom date range changes with explicit date parameters
    const handleCustomDateChange = (startDate, endDate) => {
        if (startDate && endDate) {
            setDateRange({
                type: 'custom',
                startDate: startDate,
                endDate: endDate,
            })
        }
    }

    // Auto-trigger GraphQL query when both custom dates are available
    useEffect(() => {
        const isCustomMode = dateRange === 'custom' || (typeof dateRange === 'object' && dateRange.type === 'custom')
        if (isCustomMode && customStartDate && customEndDate) {
            // Only update if the dates are actually different from what's already in dateRange
            const currentStart = typeof dateRange === 'object' ? dateRange.startDate : null
            const currentEnd = typeof dateRange === 'object' ? dateRange.endDate : null

            if (currentStart !== customStartDate || currentEnd !== customEndDate) {
                handleCustomDateChange(customStartDate, customEndDate)
            }
        }
    }, [customStartDate, customEndDate, dateRange])

    // Handle date range selection change
    const handleDateRangeChange = (value) => {
        if (value === 'custom') {
            setDateRange('custom')
        } else {
            setDateRange(value)
            setCustomStartDate('')
            setCustomEndDate('')
        }
    }

    // Generate unique ID for loading state tracking
    const getItemUniqueId = (type, item, parentIds) => {
        if (type === 'campaign') return `campaign-${item.id}`
        if (type === 'adset') return `adset-${item.db_id}`
        if (type === 'ad') return `ad-${item.id}`
        return ''
    }

    // Handle toggle active for different item types
    const handleToggleActive = async (type, item, parentIds) => {
        const itemId = getItemUniqueId(type, item, parentIds)

        // Check if already loading
        if (isItemLoading(itemId)) return

        // Start loading state
        setItemLoading(itemId, true)

        try {
            if (type === 'campaign') {
                await toggleCampaignStatus(item.id, item.active) // Use external ID for campaigns
            } else if (type === 'adset') {
                await toggleAdSetStatus(item.db_id, item.active) // Use database ID for ad sets
            } else if (type === 'ad') {
                await toggleAdStatus(item.id, item.active) // Use external ID for ads
            }
        } catch (error) {
            console.error(`Error toggling ${type} status:`, error)
        } finally {
            // Clear loading state
            setItemLoading(itemId, false)
        }
    }

    // Open delete confirmation dialog
    const handleDeleteClick = (type, item, parentIds) => {
        setDeleteConfirmation({
            isOpen: true,
            item,
            type,
            parentIds,
            isProcessing: false,
        })
    }

    // Handle delete confirmation
    const handleDeleteConfirm = async () => {
        const { type, item, parentIds } = deleteConfirmation

        setDeleteConfirmation((prev) => ({ ...prev, isProcessing: true }))

        try {
            if (type === 'campaign') {
                await deleteCampaign(item.id) // Use GraphQL ID for campaigns
            } else if (type === 'adset') {
                await deleteAdSet(item.db_id) // Use database ID for ad sets
            } else if (type === 'ad') {
                await deleteAd(item.db_id) // Use database ID for ads
            }

            // Close dialog on success
            setDeleteConfirmation({
                isOpen: false,
                item: null,
                type: null,
                parentIds: null,
                isProcessing: false,
            })
        } catch (error) {
            console.error(`Error deleting ${type}:`, error)
            // Keep dialog open on error but stop processing
            setDeleteConfirmation((prev) => ({ ...prev, isProcessing: false }))
        }
    }

    // Close delete confirmation dialog
    const handleDeleteCancel = () => {
        setDeleteConfirmation({
            isOpen: false,
            item: null,
            type: null,
            parentIds: null,
            isProcessing: false,
        })
    }

    // Navigate to the dedicated edit route for this ad campaign
    const handleEditClick = (campaign) => {
        navigate(`/campaigns/media-buying/${campaignId}/ads-manager/${campaign.id}/edit`)
    }

    const handleDuplicateClick = (campaign) => {
        navigate(`/campaigns/media-buying/${campaignId}/ads-manager/${campaign.id}/duplicate`)
    }

    const handleEditAdSetClick = (adset, parentIds) => {
        if (!parentIds?.campaignId) return
        navigate(
            `/campaigns/media-buying/${campaignId}/ads-manager/${parentIds.campaignId}/ad-sets/${adset.db_id}/edit`,
        )
    }

    const handleEditCreativeClick = (creativeId) => {
        if (!creativeId) return
        navigate(`/campaigns/media-buying/${campaignId}/edit-creative/${creativeId}`)
    }

    const renderTableRow = (item, type, level = 0, parentIds = {}) => {
        const hasChildren =
            (type === 'campaign' && item.stages?.length > 0) ||
            (type === 'stage' && item.adsets?.length > 0) ||
            (type === 'adset' && item.ads?.length > 0)

        const itemId = item.name || item.campaign_name
        const itemUniqueId = item.id || itemId
        const uniqueKey = `${type}-${itemUniqueId}-${parentIds.campaignId || ''}-${parentIds.stageId || ''}-${parentIds.adsetId || ''}`

        // Create a context-aware expansion ID to avoid conflicts between items with same names
        const expansionId = `${type}-${itemUniqueId}-${parentIds.campaignId || ''}-${parentIds.stageId || ''}-${parentIds.adsetId || ''}`
        const isExpanded = expandedItems.has(expansionId)

        // Compute metrics for stages (abstract entities)
        const displayItem = type === 'stage' ? { ...item, ...computeStageMetrics(item) } : item

        // Check if this item is currently loading
        const isLoading = isItemLoading(getItemUniqueId(type, displayItem, parentIds))

        // Increment row index and determine row color
        rowIndexRef.current++
        const currentRowIndex = rowIndexRef.current
        const isDarkRow = currentRowIndex % 2 === 1 // 1, 3, 5, etc.
        const rowBgColor = isDarkRow ? 'tw:bg-[#071727]' : 'tw:bg-[#17273C]'
        const rowBorderColor = 'tw:border-[#2B3B4C]'
        const formatAmount = (amount) => formatCurrencyAutoLocale({ amount, showCode: true, currency })

        // Get stage color for border
        const stageColor =
            type === 'stage'
                ? FUNNEL_STAGES[displayItem.name]?.color || '#D8CE41'
                : type === 'adset' && parentIds.stage
                  ? FUNNEL_STAGES[parentIds.stage]?.color || '#D8CE41'
                  : type === 'ad' && parentIds.stage
                    ? FUNNEL_STAGES[parentIds.stage]?.color || '#D8CE41'
                    : null

        if (!publisher) return ''

        const isCampaignRow = type === 'campaign'

        return (
            <div key={uniqueKey}>
                <div
                    className={cn(
                        'tw:flex tw:items-center tw:border-b tw:transition-colors tw:group tw:relative',
                        rowBgColor,
                        rowBorderColor,
                        'hover:tw:bg-[#0a1828]',
                        isCampaignRow && 'campaign-draggable-row',
                    )}
                    {...(isCampaignRow
                        ? {
                              'data-campaign-row': '',
                              'data-campaign-id': displayItem.id,
                              draggable: true,
                              tabIndex: 0,
                              role: 'row',
                              'aria-roledescription': 'Draggable campaign row. Use Alt+Up or Alt+Down to reorder.',
                              onDragStart: (e) => handleCampaignDragStart(e, displayItem.id),
                              onDragEnd: handleCampaignDragEnd,
                              onDragOver: (e) => handleCampaignDragOver(e, displayItem.id),
                              onDragLeave: handleCampaignDragLeave,
                              onDrop: (e) => handleCampaignDrop(e, displayItem.id),
                              onKeyDown: (e) => handleCampaignKeyDown(e, displayItem.id),
                          }
                        : {})}
                >
                    {(type === 'campaign' || type === 'adset' || type === 'ad') && (
                        <div
                            className={cn(
                                'tw:flex tw:items-center tw:gap-2 tw:p-4 tw:pl-6 tw:w-[100px] tw:sticky tw:left-0 tw:z-40 tw:border-l-4 tw:h-[74px]',
                                rowBgColor,
                            )}
                            style={{
                                borderLeftColor: type === 'campaign' ? (isDarkRow ? '#071727' : '#17273C') : stageColor,
                            }}
                        >
                            <label
                                className={cn(
                                    'tw:relative tw:inline-flex tw:items-center',
                                    isLoading ||
                                        (type === 'campaign' &&
                                            (displayItem.status === 'FAILED' || displayItem.status === 'PROCESSING'))
                                        ? 'tw:cursor-not-allowed tw:opacity-50'
                                        : 'tw:cursor-pointer',
                                )}
                            >
                                <input
                                    type="checkbox"
                                    className="tw:sr-only tw:peer"
                                    checked={displayItem.active}
                                    onChange={() => handleToggleActive(type, displayItem, parentIds)}
                                    disabled={
                                        isLoading ||
                                        (type === 'campaign' &&
                                            (displayItem.status === 'FAILED' || displayItem.status === 'PROCESSING'))
                                    }
                                />
                                <div
                                    className={cn(
                                        'tw:w-9 tw:h-5 tw:bg-gray-600 tw:peer-focus:outline-none tw:rounded-full tw:peer',
                                        'tw:peer-checked:after:translate-x-full tw:peer-checked:after:border-white',
                                        "tw:after:content-[''] tw:after:absolute tw:after:top-[2px] tw:after:left-[2px]",
                                        'tw:after:bg-white tw:after:rounded-full tw:after:h-4 tw:after:w-4 tw:after:transition-all',
                                        'tw:peer-checked:bg-green-600',
                                        (isLoading ||
                                            (type === 'campaign' &&
                                                (displayItem.status === 'FAILED' ||
                                                    displayItem.status === 'PROCESSING'))) &&
                                            'tw:pointer-events-none',
                                    )}
                                ></div>
                            </label>

                            {/* Show InfoIcon for failed campaigns, yellow circle for processing, normal circle for others */}
                            {type === 'campaign' && displayItem.status === 'FAILED' ? (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div>
                                            <InfoIcon size={24} className="tw:text-[#E0295E]" />
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent className="tw:max-w-80">
                                        <span>
                                            The campaign creation failed.{' '}
                                            {displayItem.error || 'Please check your campaign settings and try again.'}
                                        </span>
                                    </TooltipContent>
                                </Tooltip>
                            ) : type === 'campaign' && displayItem.status === 'PROCESSING' ? (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div
                                            className="tw:w-3 tw:h-3 tw:rounded-full"
                                            style={{ backgroundColor: '#FFD324' }}
                                        />
                                    </TooltipTrigger>
                                    <TooltipContent className="tw:max-w-80">
                                        <span>
                                            Your campaign is being processed and created on the advertising platform.
                                            This usually takes a few moments.
                                        </span>
                                    </TooltipContent>
                                </Tooltip>
                            ) : (
                                <div
                                    className={cn(
                                        'tw:w-3 tw:h-3 tw:rounded-full',
                                        displayItem.active ? 'tw:bg-green-500' : 'tw:bg-red-500',
                                        isLoading && 'tw:animate-pulse',
                                    )}
                                />
                            )}
                        </div>
                    )}
                    {type === 'stage' && (
                        <div
                            className={cn(
                                'tw:flex tw:items-center tw:gap-2 tw:p-4 tw:pl-6 tw:w-[100px] tw:sticky tw:left-0 tw:z-40 tw:border-l-4 tw:h-[74px]',
                                rowBgColor,
                            )}
                            style={{
                                borderLeftColor: stageColor,
                            }}
                        >
                            {/* Empty space to match toggle + status circle width */}
                            <div className="tw:w-9 tw:h-5 tw:invisible"></div>
                            <div className="tw:w-3 tw:h-3 tw:invisible"></div>
                        </div>
                    )}

                    <div
                        className={cn(
                            'tw:flex tw:items-center tw:gap-2 tw:p-4 tw:w-[400px] tw:sticky tw:left-[100px] tw:z-40 tw:border-r tw:border-[#2B3B4C] tw:h-[74px]',
                            rowBgColor,
                            hasChildren && 'tw:cursor-pointer hover:tw:bg-[#0a1828]',
                        )}
                        onClick={hasChildren ? () => toggleItemExpanded(expansionId) : undefined}
                    >
                        <div style={{ paddingLeft: `${level * 24}px` }} className="tw:flex tw:items-center tw:gap-2">
                            {hasChildren && (
                                <div className="tw:p-1 tw:rounded">
                                    {isExpanded ? (
                                        <ChevronDownIcon size={16} className="tw:text-[#3871FF]" />
                                    ) : (
                                        <ChevronRightIcon size={16} className="tw:text-[#3871FF]" />
                                    )}
                                </div>
                            )}
                            {!hasChildren && <div className="tw:w-6" />}

                            {type === 'campaign' &&
                                (shouldShowTooltip(displayItem.campaign_name) ? (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <span className="tw:text-sm tw:text-white tw:line-clamp-2 tw:leading-tight tw:cursor-default">
                                                {displayItem.campaign_name}
                                            </span>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>{displayItem.campaign_name}</p>
                                        </TooltipContent>
                                    </Tooltip>
                                ) : (
                                    <span className="tw:text-sm tw:text-white tw:line-clamp-2 tw:leading-tight">
                                        {displayItem.campaign_name}
                                    </span>
                                ))}
                            {type === 'campaign' && <CampaignWarning message={displayItem.warning} />}
                            {type === 'campaign' && <AdvantageLeadsNote advantageLeads={displayItem.advantage_leads} />}
                            {type === 'stage' && (
                                <div className="tw:flex tw:items-center tw:gap-2">
                                    <div
                                        className="tw:w-2 tw:h-2 tw:rounded-full"
                                        style={{ backgroundColor: FUNNEL_STAGES[displayItem.name]?.color || '#D8CE41' }}
                                    />
                                    {shouldShowTooltip(FUNNEL_STAGES[displayItem.name]?.name || displayItem.name) ? (
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <span className="tw:text-sm tw:text-white tw:line-clamp-2 tw:leading-tight tw:cursor-default">
                                                    {FUNNEL_STAGES[displayItem.name]?.name || displayItem.name}
                                                </span>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>{FUNNEL_STAGES[displayItem.name]?.name || displayItem.name}</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    ) : (
                                        <a
                                            href="#"
                                            onClick={(e) => {
                                                e.preventDefault()
                                                if (displayItem.adsets && displayItem.adsets.length > 0) {
                                                    navigate(
                                                        `/campaigns/media-buying/${campaignId}/adset-storyline/${displayItem.adsets[0].db_id}`,
                                                    )
                                                }
                                            }}
                                            className="tw:text-sm tw:text-white tw:line-clamp-2 tw:leading-tight"
                                        >
                                            {FUNNEL_STAGES[displayItem.name]?.name || displayItem.name}
                                        </a>
                                    )}
                                </div>
                            )}
                            {type === 'adset' && (
                                <div className="tw:flex tw:items-center tw:gap-2">
                                    {shouldShowTooltip(displayItem.name) ? (
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <span className="tw:text-sm tw:text-white tw:line-clamp-2 tw:leading-tight tw:cursor-default">
                                                    {displayItem.name}
                                                </span>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>{displayItem.name}</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    ) : (
                                        <span className="tw:text-sm tw:text-white tw:line-clamp-2 tw:leading-tight">
                                            {displayItem.name}
                                        </span>
                                    )}
                                    <HypothesisChip
                                        hypothesis={displayItem.audience_hypothesis}
                                        className="tw:shrink-0"
                                    />
                                    <ExclusionNote note={displayItem.exclusion_note} />
                                    <WarmAudienceNote note={displayItem.warm_audience_note} />
                                    <LookalikeNote note={displayItem.lookalike_note} />
                                    <BudgetMoveNote note={displayItem.budget_move_note} />
                                </div>
                            )}
                            {type === 'ad' && (
                                <div className="tw:flex tw:items-center tw:gap-2">
                                    {displayItem.ad_creative_id ? (
                                        <CreativeThumbnail
                                            creative={{ id: displayItem.ad_creative_id, name: displayItem.name }}
                                            size={26}
                                            showDeleteIcon={false}
                                            hoverPreview
                                            previewContent={creativeHoverPreview(displayItem.ad_creative_id)}
                                        />
                                    ) : (
                                        <div className="tw:w-6 tw:h-6 tw:bg-gray-700 tw:rounded" />
                                    )}
                                    {shouldShowTooltip(displayItem.name) ? (
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <span className="tw:text-sm tw:text-white tw:line-clamp-2 tw:leading-tight tw:cursor-default">
                                                    {displayItem.name}
                                                </span>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>{displayItem.name}</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    ) : (
                                        <span className="tw:text-sm tw:text-white tw:line-clamp-2 tw:leading-tight">
                                            {displayItem.name}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="tw:p-4 tw:pl-2 tw:min-w-[150px] tw:text-sm tw:text-white tw:flex tw:items-center tw:gap-2">
                        {getLearningPhaseIcon(displayItem.learning_phrase)}
                        <span>{displayItem.learning_phrase || ''}</span>
                    </div>

                    <LatestActionsCell actions={displayItem.latest_actions} />

                    <div className="tw:p-4 tw:pl-0 tw:min-w-[235px]">
                        {type === 'campaign' && displayItem.budget_mode && (
                            <BudgetInput
                                adset={{ db_id: displayItem.id, budget: displayItem.campaign_budget }}
                                onUpdate={updateCampaignBudget}
                                minDailyBudget={minDailyBudget}
                                currency={currency}
                            />
                        )}
                        {type === 'campaign' && !displayItem.budget_mode && (
                            <div className="tw:text-sm tw:text-white">
                                {formatAmount(computeCampaignBudget(displayItem))}
                            </div>
                        )}
                        {type === 'stage' && displayItem.uses_campaign_budget && (
                            <div className="tw:text-sm tw:text-gray-200">{CAMPAIGN_BUDGET_AD_SET_NOTE}</div>
                        )}
                        {type === 'stage' && !displayItem.uses_campaign_budget && (
                            <div className="tw:text-sm tw:text-white">{formatAmount(displayItem.budget || 0)}</div>
                        )}
                        {type === 'adset' && displayItem.uses_campaign_budget && (
                            <div className="tw:text-sm tw:text-gray-200" title={FAIR_SPLIT_NOTE}>
                                {CAMPAIGN_BUDGET_AD_SET_NOTE}
                            </div>
                        )}
                        {type === 'adset' && !displayItem.uses_campaign_budget && displayItem.budget !== undefined && (
                            <BudgetInput
                                adset={displayItem}
                                onUpdate={(adSetDbId, budget) =>
                                    updateAdSetBudget(adSetDbId, budget, displayItem.stored_budget)
                                }
                                minDailyBudget={minDailyBudget}
                                currency={currency}
                            />
                        )}
                    </div>

                    <div className="tw:p-4 tw:pl-0 tw:min-w-[170px] tw:text-sm tw:text-white">
                        {formatAmount(displayItem.amount_spent || 0)}
                    </div>

                    <div className="tw:p-4 tw:pl-0 tw:min-w-[120px] tw:text-sm tw:text-white">
                        {displayItem.roas?.toFixed(2) || '0.00'}
                    </div>

                    <div className="tw:p-4 tw:pl-0 tw:min-w-[180px] tw:text-sm tw:text-white">
                        {displayItem.cost_per_purchase && displayItem.cost_per_purchase > 0
                            ? `${formatAmount(displayItem.cost_per_purchase)}`
                            : '-'}
                    </div>

                    <div className="tw:p-4 tw:pl-0 tw:min-w-[170px] tw:text-sm tw:text-white">
                        {formatAmount(displayItem.cpm || 0)}
                    </div>

                    <div className="tw:p-4 tw:pl-0 tw:min-w-[120px] tw:text-sm tw:text-white">
                        {displayItem.ctr?.toFixed(2) || '0.00'}%
                    </div>

                    <div className="tw:p-4 tw:pl-0 tw:min-w-[120px] tw:text-sm tw:text-white">
                        {displayItem.impressions?.toLocaleString() || '0'}
                    </div>

                    <div className="tw:p-4 tw:pl-0 tw:min-w-[120px] tw:text-sm tw:text-white">
                        {displayItem.link_clicks?.toLocaleString() || '0'}
                    </div>

                    <div className="tw:p-4 tw:pl-0 tw:min-w-[120px] tw:text-sm tw:text-white">
                        {displayItem.leads?.toLocaleString() || '0'}
                    </div>

                    <div className="tw:p-4 tw:pl-0 tw:min-w-[180px] tw:text-sm tw:text-white">
                        {displayItem.cost_per_lead > 0 ? formatAmount(displayItem.cost_per_lead) : '-'}
                    </div>

                    <div className="tw:p-4 tw:pl-0 tw:min-w-[120px] tw:text-sm tw:text-white">
                        {displayItem.purchases?.toLocaleString() || '0'}
                    </div>

                    <div className="tw:p-4 tw:pl-0 tw:min-w-[170px] tw:text-sm tw:text-white">
                        {formatAmount(displayItem.revenue || 0)}
                    </div>

                    {/* Action buttons - sticky positioned at the end */}
                    {(type === 'campaign' || type === 'adset' || type === 'ad') && (
                        <div className="tw:sticky tw:right-0 tw:top-0 tw:h-full tw:flex tw:items-center tw:justify-center tw:bg-transparent tw:group-hover:flex tw:hidden tw:px-6 tw:ml-auto tw:gap-2">
                            {/* Add Audience button - only for campaigns that are not failed or processing */}
                            {type === 'campaign' &&
                                displayItem.status !== 'FAILED' &&
                                displayItem.status !== 'PROCESSING' && (
                                    <button
                                        onClick={() =>
                                            navigate(
                                                `/campaigns/media-buying/${campaignId}/ad_campaigns/${displayItem.id}/add-audience`,
                                            )
                                        }
                                        type="button"
                                        className="tw:p-1 tw:rounded tw:hover:bg-transparent tw:transition-colors"
                                        title="Add audience"
                                    >
                                        <AddAudienceIcon
                                            size={24}
                                            className="tw:cursor-pointer tw:text-blue-400 hover:tw:text-blue-300"
                                        />
                                    </button>
                                )}
                            {/* Edit button - only for campaigns that are not processing */}
                            {type === 'campaign' && displayItem.status !== 'PROCESSING' && (
                                <>
                                    <button
                                        onClick={() => handleDuplicateClick(displayItem)}
                                        type="button"
                                        className="tw:p-1 tw:rounded tw:hover:bg-transparent tw:transition-colors"
                                        title="Duplicate campaign"
                                    >
                                        <Copy className="tw:size-5 tw:cursor-pointer tw:text-blue-400 hover:tw:text-blue-300" />
                                    </button>
                                    <button
                                        onClick={() => handleEditClick(displayItem)}
                                        type="button"
                                        className="tw:p-1 tw:rounded tw:hover:bg-transparent tw:transition-colors"
                                        title="Edit campaign"
                                    >
                                        <Pencil className="tw:size-5 tw:cursor-pointer tw:text-blue-400 hover:tw:text-blue-300" />
                                    </button>
                                </>
                            )}
                            {type === 'adset' && parentIds.campaignStatus !== 'PROCESSING' && (
                                <button
                                    onClick={() => handleEditAdSetClick(displayItem, parentIds)}
                                    type="button"
                                    className="tw:p-1 tw:rounded tw:hover:bg-transparent tw:transition-colors"
                                    title="Edit ad set"
                                >
                                    <Pencil className="tw:size-5 tw:cursor-pointer tw:text-blue-400 hover:tw:text-blue-300" />
                                </button>
                            )}
                            {type === 'ad' &&
                                displayItem.ad_creative_id &&
                                parentIds.campaignStatus !== 'PROCESSING' && (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button
                                                onClick={() => handleEditCreativeClick(displayItem.ad_creative_id)}
                                                type="button"
                                                className="tw:p-1 tw:rounded tw:hover:bg-transparent tw:transition-colors"
                                            >
                                                <Pencil className="tw:size-5 tw:cursor-pointer tw:text-blue-400 hover:tw:text-blue-300" />
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent>Edit creative</TooltipContent>
                                    </Tooltip>
                                )}
                            {/* Delete button - hidden for processing campaigns */}
                            {!(type === 'campaign' && displayItem.status === 'PROCESSING') && (
                                <button
                                    onClick={() => handleDeleteClick(type, displayItem, parentIds)}
                                    type="button"
                                    className="tw:p-1 tw:rounded tw:hover:bg-transparent tw:transition-colors"
                                    title={`Delete ${type}`}
                                >
                                    <TrashIcon className="tw:size-6 tw:cursor-pointer tw:text-blue-400 hover:tw:text-blue-300" />
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {isExpanded && hasChildren && (
                    <>
                        {type === 'campaign' &&
                            item.stages?.map((stage) => (
                                <div key={`stage-${stage.name}-${item.id}`}>
                                    {renderTableRow(stage, 'stage', level + 1, {
                                        campaignId: item.id,
                                        campaign: item.campaign_name,
                                        campaignStatus: item.status,
                                    })}
                                </div>
                            ))}
                        {type === 'stage' &&
                            expandedItems.has(expansionId) &&
                            item.adsets?.map((adset) => (
                                <div key={`adset-${adset.id}`}>
                                    {renderTableRow(adset, 'adset', level + 1, {
                                        ...parentIds,
                                        stageId: item.name,
                                        stage: item.name,
                                    })}
                                </div>
                            ))}
                        {type === 'adset' &&
                            expandedItems.has(expansionId) &&
                            item.ads?.map((ad) => (
                                <div key={`ad-${ad.id}`}>
                                    {renderTableRow(ad, 'ad', level + 1, {
                                        ...parentIds,
                                        adsetId: item.id,
                                        adset: item.name,
                                    })}
                                </div>
                            ))}
                    </>
                )}
            </div>
        )
    }

    return (
        <div className="tw:bg-[#071727] tw:h-full tw:text-white tw:flex tw:flex-col tw:overflow-hidden">
            <div className="tw:flex-shrink-0">
                <div className="tw:flex tw:items-center tw:gap-1 tw:pt-4 tw:pb-4 tw:pl-6 tw:border-b tw:border-[#2B3B4C]">
                    <AdsManagerIcon size={32} />
                    <h1 className="tw:!text-[20px] tw:!font-[500] tw:!m-0 tw:!p-0">Ads Manager</h1>
                </div>

                {/* Hide controls only when showing empty state (no campaigns after successful load) */}
                {!(campaigns.length === 0 && !loading && !error) && (
                    <div className="tw:flex tw:items-center tw:gap-4 tw:p-6 tw:pb-8">
                        <Button
                            onClick={refetch}
                            variant="outline"
                            size="icon"
                            className="tw:!bg-[#2450BF33] tw:!border-[#2450BF] tw:hover:!bg-[#2450BF4D] tw:hover:!border-[#23469F] tw:transition-all"
                            disabled={loading}
                        >
                            <RefreshIcon />
                        </Button>

                        <Select
                            value={dateRange === 'custom' || typeof dateRange === 'object' ? 'custom' : dateRange}
                            onValueChange={handleDateRangeChange}
                        >
                            <SelectTrigger className="tw:w-[180px] tw:bg-[#040D16] tw:border-[#3B5168] tw:text-white">
                                <CalendarIcon size={24} />
                                <SelectValue placeholder="Select date range" />
                            </SelectTrigger>
                            <SelectContent className="tw:bg-[#040D16] tw:border-[#3B5168]">
                                <SelectItem value="last_3d" className="tw:text-white tw:hover:bg-[#1c2737]">
                                    Last 3 days
                                </SelectItem>
                                <SelectItem value="last_7d" className="tw:text-white tw:hover:bg-[#1c2737]">
                                    Last 7 days
                                </SelectItem>
                                <SelectItem value="last_14d" className="tw:text-white tw:hover:bg-[#1c2737]">
                                    Last 14 days
                                </SelectItem>
                                <SelectItem value="last_month" className="tw:text-white tw:hover:bg-[#1c2737]">
                                    Last month
                                </SelectItem>
                                <SelectItem value="last_90d" className="tw:text-white tw:hover:bg-[#1c2737]">
                                    Last 3 months
                                </SelectItem>
                                <SelectItem value="custom" className="tw:text-white tw:hover:bg-[#1c2737]">
                                    Custom
                                </SelectItem>
                            </SelectContent>
                        </Select>

                        <Select value={audienceTypeFilter} onValueChange={setAudienceTypeFilter}>
                            <SelectTrigger
                                className="tw:w-[200px] tw:bg-[#040D16] tw:border-[#3B5168] tw:text-white"
                                aria-label="Audience type"
                            >
                                <SelectValue placeholder="Audience type" />
                            </SelectTrigger>
                            <SelectContent className="tw:bg-[#040D16] tw:border-[#3B5168]">
                                <SelectItem
                                    value={AUDIENCE_TYPE_FILTERS.ALL}
                                    className="tw:text-white tw:hover:bg-[#1c2737]"
                                >
                                    All audiences
                                </SelectItem>
                                <SelectItem
                                    value={AUDIENCE_TYPE_FILTERS.AI}
                                    className="tw:text-white tw:hover:bg-[#1c2737]"
                                >
                                    AI audiences
                                </SelectItem>
                                <SelectItem
                                    value={AUDIENCE_TYPE_FILTERS.EXISTING}
                                    className="tw:text-white tw:hover:bg-[#1c2737]"
                                >
                                    Existing audiences
                                </SelectItem>
                            </SelectContent>
                        </Select>

                        {/* Custom Date Range Pickers */}
                        {(dateRange === 'custom' || typeof dateRange === 'object') && (
                            <div className="tw:flex tw:items-center tw:gap-2">
                                <DatePicker
                                    value={customStartDate}
                                    onChange={setCustomStartDate}
                                    placeholder="Start date"
                                    className="tw:w-[150px]"
                                    hideIcon
                                    autoSave
                                    allowPastDates={true}
                                />
                                <DatePicker
                                    value={customEndDate}
                                    onChange={setCustomEndDate}
                                    placeholder="End date"
                                    className="tw:w-[150px]"
                                    hideIcon
                                    autoSave
                                    allowPastDates={true}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="tw:flex-1 tw:min-h-0 tw:relative">
                {loading ? (
                    <div className="tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center tw:bg-[#071727] tw:z-20">
                        <CubeLoader size="default" fullScreen={true} />
                    </div>
                ) : error ? (
                    <div className="tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center tw:bg-[#071727] tw:z-20">
                        <div className="tw:text-red-500 tw:text-lg">Error: {error.message}</div>
                    </div>
                ) : campaigns.length === 0 ? (
                    <div className="tw:absolute tw:inset-0 tw:flex tw:flex-col tw:items-center tw:justify-center tw:bg-[#071727] tw:z-20 tw:gap-6">
                        <div className="tw:rounded-full tw:bg-[#2B3B4C] tw:flex tw:items-center tw:justify-center tw:size-[80px]">
                            <MegaphoneIcon size={56} />
                        </div>
                        <p className="tw:text-white tw:text-center tw:max-w-sm tw:text-sm tw:font-normal tw:!mb-0">
                            This section will be populated
                            <br />
                            once you launch your first
                            <br />
                            media buying campaign.
                        </p>
                        <Button
                            onClick={() => navigate(`/campaigns/media-buying/${campaignId}/create-new`)}
                            className="tw:bg-[#2450BF] tw:hover:bg-[#2860EE] tw:text-white tw:px-6 tw:py-2 tw:rounded tw:flex tw:items-center tw:gap-1 tw:font-medium  tw:text-sm"
                        >
                            <PlusIcon className="tw:size-6" />
                            New Ad Campaign
                        </Button>
                    </div>
                ) : (
                    <div className="tw:absolute tw:inset-0 tw:overflow-x-auto tw:overflow-y-auto">
                        <div className="tw:w-max">
                            <div className="tw:flex tw:items-center tw:border-b tw:border-[#2B3B4C] tw:bg-[#071727] tw:sticky tw:top-0 tw:z-50">
                                <div className="tw:p-4 tw:pl-6 tw:w-[100px] tw:font-semibold tw:text-sm tw:text-white tw:sticky tw:left-0 tw:z-50 tw:bg-[#071727] tw:border-l-4 tw:border-l-[#071727] tw:flex tw:items-center tw:h-[74px]">
                                    On/Off
                                </div>
                                <div className="tw:p-4 tw:pl-6 tw:w-[400px] tw:font-semibold tw:text-sm tw:text-white tw:sticky tw:left-[100px] tw:z-50 tw:bg-[#071727]">
                                    Asset Name
                                </div>
                                <div className="tw:p-4 tw:pl-2 tw:min-w-[150px] tw:font-semibold tw:text-sm tw:text-white">
                                    Status
                                </div>
                                <div className="tw:p-4 tw:pl-0 tw:min-w-[180px] tw:font-semibold tw:text-sm tw:text-white">
                                    Latest Actions
                                </div>
                                <div className="tw:p-4 tw:pl-0 tw:min-w-[235px] tw:font-semibold tw:text-sm tw:text-white">
                                    Budget
                                </div>
                                <div className="tw:p-4 tw:pl-0 tw:min-w-[170px] tw:font-semibold tw:text-sm tw:text-white">
                                    Amount Spent
                                </div>
                                <div className="tw:p-4 tw:pl-0 tw:min-w-[120px] tw:font-semibold tw:text-sm tw:text-white">
                                    ROAS
                                </div>
                                <div className="tw:p-4 tw:pl-0 tw:min-w-[180px] tw:font-semibold tw:text-sm tw:text-white">
                                    Cost per Purchase
                                </div>
                                <div className="tw:p-4 tw:pl-0 tw:min-w-[170px] tw:font-semibold tw:text-sm tw:text-white">
                                    CPM
                                </div>
                                <div className="tw:p-4 tw:pl-0 tw:min-w-[120px] tw:font-semibold tw:text-sm tw:text-white">
                                    CTR
                                </div>
                                <div className="tw:p-4 tw:pl-0 tw:min-w-[120px] tw:font-semibold tw:text-sm tw:text-white">
                                    Impressions
                                </div>
                                <div className="tw:p-4 tw:pl-0 tw:min-w-[120px] tw:font-semibold tw:text-sm tw:text-white">
                                    Link Clicks
                                </div>
                                <div className="tw:p-4 tw:pl-0 tw:min-w-[120px] tw:font-semibold tw:text-sm tw:text-white">
                                    Leads
                                </div>
                                <div className="tw:p-4 tw:pl-0 tw:min-w-[180px] tw:font-semibold tw:text-sm tw:text-white">
                                    Cost per Lead
                                </div>
                                <div className="tw:p-4 tw:pl-0 tw:min-w-[120px] tw:font-semibold tw:text-sm tw:text-white">
                                    Purchases
                                </div>
                                <div className="tw:p-4 tw:pl-0 tw:min-w-[170px] tw:font-semibold tw:text-sm tw:text-white">
                                    Revenue
                                </div>
                            </div>

                            <div>
                                {(() => {
                                    // Reset row index before rendering table
                                    rowIndexRef.current = 0
                                    return campaigns.map((campaign) => renderTableRow(campaign, 'campaign', 0, {}))
                                })()}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Delete Confirmation Dialog */}
            <ConfirmationDialog
                isOpen={deleteConfirmation.isOpen}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                title="Delete"
                description={
                    deleteConfirmation.item
                        ? `If you delete this ${deleteConfirmation.item.name || deleteConfirmation.item.campaign_name || 'item'} you will not be able to view their historical performance data anymore.`
                        : 'Are you sure you want to delete this item?'
                }
                confirmText="Yes, confirm"
                cancelText="Cancel"
                variant="destructive"
                isProcessing={deleteConfirmation.isProcessing}
            />
        </div>
    )
})

export default AdsManager
